import React, { useEffect, useState, createRef } from "react";
import { makeStyles } from "@material-ui/core";
import { Chart, CommonSeriesSettings, Legend, ArgumentAxis, Label, Series, Tooltip, ValueAxis, Grid, MinorGrid, CommonPaneSettings, Export, Border, ZoomAndPan } from "devextreme-react/chart";
import { controlTypes, externalApiTypes, timeLineVisualisationTypes } from "../../../../Config/constants";
import { parseNewDateToDDMMYYYnoTime } from "../../../../Utils/dateHelper";
import { ArrowRight } from "lucide-react";

export default function FilterListRow({ loading, ...props }) {
  const classes = useStyles();
  const [visualStartTime, setVisualStartTime] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Subtract 1 day
    return yesterday;
  });
  const [visualEndTime, setVisualEndTime] = useState(new Date());
  const [labelData, setLabelData] = useState([]);

  const [visualisationType, setVisualisationType] = useState(timeLineVisualisationTypes.SPLIT);

  useEffect(() => {
    if (props.visualisationType) {
      setVisualisationType(props.visualisationType);
    }
  }, [props.visualisationType]);

  useEffect(() => {
    setLabelData([]);
  }, [props.timeRange, props.refresh, props.endValue, props.startValue]);

  function getRGB(c) {
    return parseInt(c, 16) || c;
  }

  function getsRGB(c) {
    return getRGB(c) / 255 <= 0.03928 ? getRGB(c) / 255 / 12.92 : Math.pow((getRGB(c) / 255 + 0.055) / 1.055, 2.4);
  }

  function getLuminance(hexColor) {
    if (hexColor.includes("#") && hexColor !== undefined) {
      return 0.2126 * getsRGB(hexColor.substr(1, 2)) + 0.7152 * getsRGB(hexColor.substr(3, 2)) + 0.0722 * getsRGB(hexColor.substr(-2));
    } else {
      return 0.2126 * hexColor + 0.7152 * hexColor + 0.0722 * hexColor;
    }
  }

  function getContrast(f, b) {
    const L1 = getLuminance(f);
    const L2 = getLuminance(b);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
  }

  function getTextColor(bgColor) {
    if (bgColor === null) {
      return "#ffffff";
    } else {
      const whiteContrast = getContrast(bgColor, "#ffffff");
      const blackContrast = getContrast(bgColor, "#000000");
      return whiteContrast > blackContrast ? "#ffffff" : "#000000";
    }
  }

  function customizeValueAxis(e) {
    if (e.valueText.includes("PM") || e.valueText.includes("AM")) {
      let date = new Date(e.value);
      let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
      let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();
      let seconds = date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds();
      let time = hours + ":" + minutes + ":" + seconds;
      let htmlItem = "";
      htmlItem += '<div style="padding-right: 5px">' + time + "</div><br>";
      return htmlItem;
    } else {
      if (e.valueText.includes("s")) {
        let date = new Date(e.value);
        let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
        let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();
        let seconds = date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds();
        let time = hours + ":" + minutes + ":" + seconds;
        let htmlItem = "";
        htmlItem += '<div style="padding-right: 5px">' + time + "</div><br>";
        return htmlItem;
      } else {
        let date = new Date(e.value);
        let strDay = date.toLocaleDateString("en-UK", {
          weekday: "long",
        });
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Adjust for zero-based month
        const year = date.getFullYear();
        // Build the HTML string using template literals
        const htmlItem = `<div>${strDay}</div><div>${day}/${month}/${year}</div>`;
        return htmlItem;
      }
    }
  }

  function customizeTooltip(pointInfo) {
    let strTime = pointInfo.valueText;
    let insertedOn = pointInfo.point.data.insertedOn;
    let updatedOn = pointInfo.point.data.updatedOn;
    let times = strTime.split(" - ");
    let startTime = times[0];

    var date = parseNewDateToDDMMYYYnoTime(new Date(startTime));

    let startDateTime = new Date(pointInfo.point.data.startDate);
    let endDateTime = new Date(pointInfo.point.data.endDate);

    let endTime = times[1];
    let value = "";
    let strStartTime = convertToTimeString(startTime);
    let strEndTime = convertToTimeString(endTime);
    let time = "";
    let commentText = pointInfo.point.data.commentText;
    var commentImage;
    var commentImageFound = false;
    if (pointInfo.point.data.commentImage !== "" && pointInfo.point.data.commentImage !== null) {
      commentImageFound = true;
      commentImage = props.data.find((x) => x.id === pointInfo.point.data.id).commentImage;
    }
    if (pointInfo.point.data.typeId === controlTypes.STOPWATCH || (pointInfo.point.data.typeId === controlTypes.EXTERNALAPI && pointInfo.point.data.externalApiTypeId === externalApiTypes.STOPWATCH)) {
      strStartTime = convertToTimeString(startDateTime);
      strEndTime = convertToTimeString(endDateTime);

      value = pointInfo.point.data.measurementValue;
      time = strStartTime + " - " + strEndTime;
    } else {
      value = pointInfo.point.data.measurementValue;
      time = strStartTime;
    }
    let text = "";
    if (commentImageFound) {
      text += '<div style="min-height: 350px;">';
    } else {
      text += "<div>";
    }
    text += `<div><b>Project:</b> ${pointInfo.point.data.projectName}</div>`;
    text += `<div><b>Company: </b>${pointInfo.point.data.companyName}</div>`;
    text += `<div><b>Registration point: </b>${pointInfo.point.data.resource}</div>`;
    text += `<div><b>Time: </b>${time}</div>`;
    text += `<div><b>Date: </b>${date}</div>`;
    text += `<div><b>Key: </b>${pointInfo.point.data.key !== null ? pointInfo.point.data.key : "-"}</div>`;

    if (pointInfo.point.data.commentText !== "" && pointInfo.point.data.commentText !== null) {
      text += `<div style="padding-bottom: 5px"><b>Comment: </b>${commentText}</div>`;
    }
    if (commentImageFound) {
      text += `<div style="padding-bottom: 5px"><b>Comment Image: </b></div>`;
      text += `<img style="border-radius: 5px; max-width: 100%; max-height: calc(20vh - 16px);" src="data:image/png;base64,${commentImage}"></img>`;
    }

    if (pointInfo.point.data.typeId === controlTypes.CAMERA && pointInfo.point.data.thumbnail !== null) {
      text += `<img  src="data:image/png;base64,${pointInfo.point.data.thumbnail}"></img>`;
    }
    text += "</div>";

    return { text: `${pointInfo.argumentText}<br/> ${time}`, html: text, visible: true };
  }

  function customizeLinearTooltip(pointInfo) {
    let strTime = pointInfo.valueText;
    let insertedOn = pointInfo.point.data.insertedOn;
    let updatedOn = pointInfo.point.data.updatedOn;
    let times = strTime.split(" - ");
    let startTime = times[0];

    var date = parseNewDateToDDMMYYYnoTime(new Date(startTime));

    let startDateTime = new Date(pointInfo.point.data.startDate);
    let endDateTime = new Date(pointInfo.point.data.endDate);

    let endTime = times[1];
    let value = "";
    let strStartTime = convertToTimeString(startTime);
    let strEndTime = convertToTimeString(endTime);
    let time = "";
    let commentText = pointInfo.point.data.commentText;
    var commentImage;
    var commentImageFound = false;
    if (pointInfo.point.data.commentImage !== "" && pointInfo.point.data.commentImage !== null) {
      commentImageFound = true;
      commentImage = props.data.find((x) => x.id === pointInfo.point.data.id).commentImage;
    }
    if (pointInfo.point.data.typeId === controlTypes.STOPWATCH || (pointInfo.point.data.typeId === controlTypes.EXTERNALAPI && pointInfo.point.data.externalApiTypeId === externalApiTypes.STOPWATCH)) {
      strStartTime = convertToTimeString(startDateTime);
      strEndTime = convertToTimeString(endDateTime);

      value = pointInfo.point.data.measurementValue;
      time = strStartTime + " - " + strEndTime;
    } else {
      value = pointInfo.point.data.measurementValue;
      time = strStartTime;
    }
    let text = "";
    if (commentImageFound) {
      text += '<div style="min-height: 350px;">';
    } else {
      text += "<div>";
    }
    text += `<div><b>Value:</b> ${pointInfo.point.data.measurementValue}</div>`;
    text += `<div><b>Project:</b> ${pointInfo.point.data.projectName}</div>`;
    text += `<div><b>Company: </b>${pointInfo.point.data.companyName}</div>`;
    text += `<div><b>Registration point: </b>${pointInfo.point.data.resource}</div>`;
    text += `<div><b>Time: </b>${time}</div>`;
    text += `<div><b>Date: </b>${date}</div>`;
    text += `<div><b>Key: </b>${pointInfo.point.data.key !== null ? pointInfo.point.data.key : "-"}</div>`;

    if (pointInfo.point.data.commentText !== "" && pointInfo.point.data.commentText !== null) {
      text += `<div style="padding-bottom: 5px"><b>Comment: </b>${commentText}</div>`;
    }
    if (commentImageFound) {
      text += `<div style="padding-bottom: 5px"><b>Comment Image: </b></div>`;
      text += `<img style="border-radius: 5px; max-width: 100%; max-height: calc(20vh - 16px);" src="data:image/png;base64,${commentImage}"></img>`;
    }

    if (pointInfo.point.data.typeId === controlTypes.CAMERA && pointInfo.point.data.thumbnail !== null) {
      text += `<img  src="data:image/png;base64,${pointInfo.point.data.thumbnail}"></img>`;
    }
    text += "</div>";

    return { text: `${pointInfo.argumentText}<br/> ${time}`, html: text, visible: true };
  }

  function customizePoint(pointInfo) {
    if (pointInfo.data.typeId === controlTypes.CAMERA && pointInfo.data.thumbnail !== null) {
      return { image: { url: "data:image/png;base64," + pointInfo.data.thumbnail, width: 50, height: 50 }, visible: true };
    } else {
      return { color: this.data.color, border: { color: "grey", width: 1, visible: true, dashStyle: "dot", opacity: 1 } };
    }
  }

  function convertToTimeString(time) {
    let dateTime = new Date(time);
    let strTime = dateTime.toTimeString();
    strTime = strTime.split(" ")[0];
    return strTime;
  }

  function customizeText(e) {
    let tempLabelObj = labelData;
    let tempObj = {};
    let text = "";
    let color = "";
    if (e.point.data.color !== undefined) {
      color = getTextColor(e.point.data.color);
    } else {
      color = "black";
    }

    if (e.point.data.typeId === controlTypes.STOPWATCH || (e.point.data.typeId === controlTypes.EXTERNALAPI && e.point.data.externalApiTypeId === externalApiTypes.STOPWATCH)) {
      //Stopwatch
      if (e.index === 0 && e.point.data.measurementValue !== "" && e.point.data.startDate !== null && e.point.data.endDate !== null) {
        let startDateTime = new Date(e.point.data.startDate);
        let endDateTime = new Date(e.point.data.endDate);
        let strStartTime = convertToTimeString(startDateTime);
        let strEndTime = convertToTimeString(endDateTime);

        tempObj = { id: e.point.data.id };
        tempLabelObj.push(tempObj);
        return `<div style="color: ${color}">${strStartTime} - ${strEndTime}</div>`;
      }
    } else {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.measurementValue + "</div>")}`;
    }
  }

  function measurementKeyLabel(e) {
    let tempLabelObj = labelData;
    let tempObj = {};
    let text = "";
    let color = "";
    if (e.point.data.color !== undefined) {
      color = getTextColor(e.point.data.color);
    } else {
      color = "black";
    }
    // Check if measurementPoint is a key value
    if (e.point.data.typeId === 3) {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.measurementValue + "</div>")}`;
    } else if (e.point.data.key !== null) {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.key + "</div>")}`;
    } else {
      return `${(text += '<div style="color: ' + color + '; background: none">' + e.point.data.measurementValue + "</div>")}`;
    }
  }

  useEffect(() => {
    if (props.data && props.data.length > 0) {
      const allStartDates = props.data.map((item) => new Date(item.startDate));
      const allEndDates = props.data.map((item) => new Date(item.endDate ?? item.startDate));

      const minDate = new Date(Math.min(...allStartDates));
      const maxDate = new Date(Math.max(...allEndDates));

      // Add a little padding if needed (optional)
      const paddedMin = new Date(minDate.getTime() - 1 * 60 * 1000); // minus 1 minute
      const paddedMax = new Date(maxDate.getTime() + 1 * 60 * 1000); // plus 1 minute

      setVisualStartTime(paddedMin);
      setVisualEndTime(paddedMax);
    } else if (props.from && props.to) {
      // fallback to full range if no data
      setVisualStartTime(new Date(props.from.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")));
      setVisualEndTime(new Date(props.to.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")));
    }
  }, [props.data, props.from, props.to]);

  const chartRef = createRef();
  return (
    <>
      {loading && (
        <div className={classes.loadingOverlay}>
          <div className={classes.loadingCard}>
            <div className={classes.loadingSpinner}></div>
            <p className={classes.loadingCard__title}>Loading chart...</p>
          </div>
        </div>
      )}
      <div className={classes.chartBox}>
        <Chart
          id="chart"
          dataSource={props.data}
          rotated={true}
          customizePoint={customizePoint}
          // resolveLabelOverlapping="hide"
          title={`Evaluate your events`}
          ref={chartRef}
        >
          <CommonSeriesSettings
            type="rangeBar"
            value="measurementValue"
            argumentField="description"
            rangeValue1Field="startDate"
            rangeValue2Field="endDate"
            barOverlapGroup="description"
          ></CommonSeriesSettings>
          <Legend visible={false} />

          {/* SC: Series for the stopwatches */}
          <Series
            type={visualisationType === timeLineVisualisationTypes.SPLIT ? "rangeBar" : "scatter"}
            valueField="startDate"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "description" : "linearTimeline"}
            // argumentField={"description"}
          >
            <Label position={"inside"} visible={true} indent={30} customizeText={visualisationType === timeLineVisualisationTypes.SPLIT ? customizeText : measurementKeyLabel} />
          </Series>
          {/* SC: Series for the counters */}
          <Series
            type="scatter"
            valueField="startDate"
            // argumentField="counterDescription"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "counterDescription" : "linearTimeline"}
            data="measurementValue"
          >
            <Label position={"inside"} visible={true} customizeText={visualisationType === timeLineVisualisationTypes.SPLIT ? customizeText : measurementKeyLabel} />
          </Series>
          {/* SC: Series for the free text */}
          <Series
            type="scatter"
            valueField="startDate"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "freetextDescription" : "linearTimeline"}
            // argumentField="freetextDescription"
            data="measurementValue"
          >
            <Label visible={true} customizeText={visualisationType === timeLineVisualisationTypes.SPLIT ? customizeText : measurementKeyLabel} />
          </Series>
          {/* SC: Series for the datalist */}
          <Series
            type="scatter"
            valueField="startDate"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "datalistDescription" : "linearTimeline"}
            // argumentField="datalistDescription"
            data="measurementValue"
          >
            <Label location="left" alignment="right" visible={true} customizeText={visualisationType === timeLineVisualisationTypes.SPLIT ? customizeText : measurementKeyLabel} />
          </Series>
          {/* SC: Series for the image */}
          <Series
            type="scatter"
            valueField="startDate"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "cameraDescription" : "linearTimeline"}
            // argumentField="cameraDescription"
            data="measurementValue"
          ></Series>
          {/* SC: Series for the ExternalApiValues */}
          <Series
            type="scatter"
            valueField="startDate"
            argumentField={visualisationType === timeLineVisualisationTypes.SPLIT ? "externalApiDescription" : "linearTimeline"}
            // argumentField="externalApiDescription"
            data="measurementValue"
          >
            <Label visible={true} customizeText={visualisationType === timeLineVisualisationTypes.SPLIT ? customizeText : measurementKeyLabel} />
          </Series>
          <Tooltip enabled={true} customizeTooltip={visualisationType === timeLineVisualisationTypes.SPLIT ? customizeTooltip : customizeLinearTooltip} />
          <ValueAxis
            visualRange={{
              startValue: visualStartTime.toISOString(),
              endValue: visualEndTime.toISOString(),
            }}
          >
            <Grid visible={true} />
            <Label customizeText={customizeValueAxis} />
          </ValueAxis>
          <ArgumentAxis
            tickInterval={5}
            visualRange={{
              startValue: visualStartTime.toISOString(),
              endValue: visualEndTime.toISOString(),
            }}
          >
            <Grid visible={true} />
            <MinorGrid visible={true} />
          </ArgumentAxis>

          <Export enabled={true} />

          <CommonPaneSettings>
            <Border visible={true} />
          </CommonPaneSettings>
          <ZoomAndPan panKey="shift" argumentAxis="none" valueAxis="both" allowMouseWheel={true} dragToZoom="true" />
        </Chart>
      </div>
    </>
  );
}

const useStyles = makeStyles(() => ({
  chart: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  chartBox: {
    padding: "10px",
  },
  emptyStateContainer: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "998",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyStateCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  emptyStateCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginBottom: "8px",
  },

  emptyStateCard__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 0.8)",

    textAlign: "center",

    margin: "0px",
  },

  loadingOverlay: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "999",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  loadingCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  loadingCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginTop: "16px",
  },

  loadingSpinner: {
    width: "32px",
    height: "32px",
    border: "3px solid rgba(12, 14, 41, 0.1)",
    borderTop: "3px solid rgba(12, 14, 41, 0.7)",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));
